import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WhatsAppFab } from "@/components/landing/WhatsAppFab";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { SITE, IMAGES } from "@/lib/site";
import { useCart } from "@/lib/cart";
import { RING_SIZES, sizingFee, stockSizeOf, prettySize } from "@/lib/sizing";
import { productPath, skuFromSlug, slugWithSku, displaySku, useSEO, SITE_ORIGIN } from "@/lib/seo";
import { fetchWithRetry, isNotFound } from "@/lib/fetchRetry";
import { displayTitle, isMontagePrimary } from "@/lib/title";
import { specList, keySpecs } from "@/lib/specs";
import { aboutPiece } from "@/lib/about";
import { trackViewItem, trackSelectItem } from "@/lib/analytics";
import { recordView } from "@/lib/recentlyViewed";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { TrustBar, FinancingNote } from "@/components/TrustBar";
import { ProductReviews, Stars } from "@/components/ProductReviews";
import { WatchBuyerBanner } from "@/components/landing/WatchBuyerBanner";
import { ShoppingBag, Zap, MessageCircle, ChevronRight, PenLine, FileText } from "lucide-react";
import { WishlistButton } from "@/components/WishlistButton";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGRAVING_FEE = 49.99;

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

// Short factual line under the price (P1.3) — built only from known feed
// attributes; no craftsmanship, origin or certification claims.
const DESC_BASES = [
  "feature", "finish", "chain type", "clasp /connector", "earring closure",
  "ring type", "bracelet type", "pendant/charm type", "stone type", "stone shape",
];

const describe = (p) => {
  const picks = specList(p)
    .filter((s) => DESC_BASES.includes(s.base))
    .map((s) => s.value);
  const uniq = [...new Set(picks)].slice(0, 4);
  const metal = p.np_material || p.metal; // N1: customer-facing label, not feed codes
  if (uniq.length) return [metal, ...uniq].filter(Boolean).join(" · ");
  return p.description || metal || "";
};

// "About this piece" copy comes from lib/about.js (P2.2) — natural prose
// composed only from known product data; raw key/value dumps are gone.

// Human title recovered from the URL's keyword half (the slug was generated
// from the real product title) — used only for the transient-error head tags.
const slugTitle = (slug) => {
  const words = (slug || "").split("--")[0].replace(/-/g, " ").trim();
  return words ? words.replace(/\b\w/g, (c) => c.toUpperCase()) : "Fine Jewelry";
};

export default function ProductPage() {
  const { sku: slugParam } = useParams();
  const sku = skuFromSlug(slugParam || "");
  const navigate = useNavigate();
  const cart = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false); // transient API failure — NEVER noindex
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [zoomOrigin, setZoomOrigin] = useState(null); // "x% y%" while hovering (desktop zoom)
  const touchStartX = useRef(null);
  const [otherSize, setOtherSize] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [wantEngraving, setWantEngraving] = useState(false);
  const [engraveText, setEngraveText] = useState("");
  const [plateName, setPlateName] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteDone, setQuoteDone] = useState(false);
  // N3: optional chain add-on (real catalog chains only)
  const [chainData, setChainData] = useState(null);
  const [chainSel, setChainSel] = useState({ style: null, length: null });
  const [manualImg, setManualImg] = useState(null); // gallery thumb override
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setNotFound(false);
    setLoadError(false);
    setActiveImg(0);
    setManualImg(null); // gallery thumb override must not carry across SKUs
    setZoomOrigin(null);
    setQuantity(1);
    setOtherSize(false);
    setSelectedSize(null);
    setChainSel({ style: null, length: null });
    setChainData(null);
    let cancelled = false;
    // 2026-10-11 noindex incident: notFound (noindex) ONLY on a real HTTP 404.
    // Timeouts/5xx/429/network drops retry with backoff, then a recoverable
    // "temporarily unavailable" state — never "Piece Not Found", never noindex.
    fetchWithRetry(`${API}/products/${encodeURIComponent(sku)}`)
      .then((r) => {
        if (cancelled) return;
        setProduct(r.data);
        trackViewItem(r.data);
        recordView(r.data, productPath(r.data));
      })
      .catch((err) => {
        if (cancelled) return;
        if (isNotFound(err)) setNotFound(true); else setLoadError(true);
      });
    axios
      .get(`${API}/products/related`, { params: { sku, limit: 8 } })
      .then((r) => setRelated(r.data.items || []))
      .catch(() => setRelated([]));
    return () => { cancelled = true; };
  }, [sku]);

  const [reviewSummary, setReviewSummary] = useState(null);

  useEffect(() => {
    if (!product?.sku) return;
    axios
      .get(`${API}/reviews/${encodeURIComponent(product.sku)}`)
      .then((r) => setReviewSummary(r.data.summary))
      .catch(() => {});
  }, [product?.sku]);

  const title = product ? displayTitle(product) : "";

  useSEO(
    notFound
      ? { title: "Piece Not Found | Gold Time Jewelry", canonicalPath: `/catalog/${slugParam}`, noindex: true }
      : loadError
        ? {
            // Transient failure head: indexable, honest title from the URL's own
            // keyword half (generated from the real product title) — no noindex.
            title: `${slugTitle(slugParam)} | Gold Time Jewelry`,
            canonicalPath: `/catalog/${slugParam}`,
          }
      : product
        ? (() => {
            const canonicalPath =
              product.nameplate && product.family_rep_sku && product.family_rep_sku !== product.sku
                ? productPath({ ...product, sku: product.family_rep_sku })
                : productPath(product);
            return {
              title: `${title} | Gold Time Jewelry`,
              description: `${title} — ${money(product.retail_price)} at Gold Time Jewelry, Texas. ${product.metal || "Fine jewelry"}. Insured direct shipping or ship-to-showroom pickup.`,
              canonicalPath,
              ogType: "product",
              ogImage: product.image_url,
              jsonLd: {
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@type": "Product",
                    name: title,
                    sku: product.sku,
                    image: (product.images?.length ? product.images : [product.image_url]).filter(Boolean),
                    description: product.description || title,
                    // Private-label model (P2.3, owner-corrected): all
                    // catalog products are Gold Time store-brand; MPN is the
                    // real style number shown on this page.
                    brand: { "@type": "Brand", name: "Gold Time Jewelry" },
                    mpn: displaySku(product.sku),
                    ...(reviewSummary && reviewSummary.count > 0
                      ? {
                          aggregateRating: {
                            "@type": "AggregateRating",
                            ratingValue: reviewSummary.average,
                            reviewCount: reviewSummary.count,
                          },
                        }
                      : {}),
                    offers: {
                      "@type": "Offer",
                      url: `${SITE_ORIGIN}${canonicalPath}`,
                      priceCurrency: "USD",
                      price: product.retail_price,
                      itemCondition: "https://schema.org/NewCondition",
                      availability:
                        product.qty_avail > 0
                          ? "https://schema.org/InStock"
                          : "https://schema.org/BackOrder",
                    },
                  },
                  {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
                      { "@type": "ListItem", position: 2, name: "Catalog", item: `${SITE_ORIGIN}/catalog` },
                      ...(product.category
                        ? [{ "@type": "ListItem", position: 3, name: product.category, item: `${SITE_ORIGIN}/catalog` }]
                        : []),
                      { "@type": "ListItem", position: product.category ? 4 : 3, name: title },
                    ],
                  },
                ],
              },
            };
          })()
        : { canonicalPath: `/catalog/${slugParam}` }
  );

  const isNameplate =
    !!product && (product.nameplate === true || /name\s*plate/i.test(product.name || ""));
  const plate = isNameplate && plateName.trim() ? plateName.trim() : null;
  // N3: per-family merchandising mode. Type A ("quote") = variable-size
  // design, always quoted personally, price hidden. Type B ("fixed") =
  // fixed plate, direct purchase for 1–9 chars at the exact SKU price.
  const npMode = isNameplate ? product.np_mode || "quote" : null;
  const isQuoteMode = npMode === "quote";
  const plateLen = plateName.trim().length;
  const needsQuote = isNameplate && (isQuoteMode || plateLen > 9);

  const addToCart = () => {
    if (isNameplate && !plate) {
      toast.error("Tell us the name for your name plate.");
      return;
    }
    if (needsQuote) {
      setQuoteOpen(true);
      return;
    }
    if (wantEngraving && !engraveText.trim()) {
      toast.error("Tell us what to engrave — up to 30 characters.");
      return;
    }
    cart.add(cartProduct, quantity, sizing, engraving, plate,
           !sizing && cartProduct?.variant_axis === "size" && cartProduct?.variant_value
             ? cartProduct.variant_label || `Size ${cartProduct.variant_value}`
             : null);
    // N3: optional chain is a separate, identifiable cart line at its own
    // real SKU price — never bundled into the nameplate price.
    if (selectedChain) {
      cart.add({
        sku: selectedChain.sku, name: selectedChain.display_name, seo_title: null,
        retail_price: selectedChain.price, image_url: selectedChain.image_url,
      }, 1);
    }
    toast.success(
      plate
        ? `Added — name plate "${plate}"${selectedChain ? " + chain" : ""}`
        : engraving
          ? `Added — engraving "${engraving.text}" (+${money(ENGRAVING_FEE)})`
          : sizing
            ? `Added — sized to ${prettySize(sizing.size)}`
            : "Added to your cart"
    );
  };

  const variantOptions = product
    ? [
        { sku: product.sku, variant_label: product.variant_label, variant_value: product.variant_value, retail_price: product.retail_price },
        ...(product.variants || []),
      ].sort((a, b) => (parseFloat(a.variant_value) || 0) - (parseFloat(b.variant_value) || 0))
    : [];

  // ---------- N1: nameplate family configurator ----------
  // Family members = this doc + its np siblings — all REAL feed SKUs.
  // Material → Color selections resolve to an exact existing SKU; price and
  // image always come from that SKU, never computed on the frontend.
  const npFamily = useMemo(() => {
    if (!product?.nameplate || !product?.np_family) return null;
    const bySku = new Map([[product.sku, product], ...(product.variants || []).map((v) => [v.sku, v])]);
    const list = [...bySku.values()];
    const ORDER = ["Sterling Silver", "Gold-Plated Sterling Silver", "Rose-Plated Sterling Silver", "10K Gold", "14K Gold"];
    const materials = [...new Set(list.map((m) => m.np_material).filter(Boolean))].sort(
      (a, b) => ORDER.indexOf(a) - ORDER.indexOf(b)
    );
    return { list, materials };
  }, [product]);

  const [npSel, setNpSel] = useState({ material: null, color: null });
  useEffect(() => {
    if (product?.np_family) setNpSel({ material: product.np_material || null, color: product.np_color || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.sku]);

  const npColors = npFamily && npSel.material
    ? [...new Set(npFamily.list.filter((m) => m.np_material === npSel.material).map((m) => m.np_color).filter(Boolean))]
    : [];
  const npResolved = npFamily
    ? npFamily.list.find(
        (m) => m.np_material === npSel.material && (m.np_color || null) === (npColors.length ? npSel.color : null)
      )
    : null;
  const pickNpMaterial = (mat) => {
    const colors = [...new Set(npFamily.list.filter((m) => m.np_material === mat).map((m) => m.np_color).filter(Boolean))];
    setNpSel({ material: mat, color: colors[0] || null });
    setManualImg(null);
  };
  // N3: chain options depend on the resolved SKU (chain inclusion differs per
  // member). Quote-mode PDPs also show the selector — the choice travels with
  // the quote request instead of the cart.
  const npTargetSku = npResolved?.sku || product?.sku;
  useEffect(() => {
    setChainSel({ style: null, length: null });
    setChainData(null);
    if (!isNameplate || !npTargetSku) return;
    axios
      .get(`${API}/nameplate-chains`, { params: { sku: npTargetSku } })
      .then((r) => setChainData(r.data))
      .catch(() => setChainData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npTargetSku, isNameplate]);
  const chainStyleEntry = chainSel.style && chainData
    ? chainData.styles.find((s) => s.style === chainSel.style) || null
    : null;
  const selectedChain = chainStyleEntry && chainSel.length
    ? chainStyleEntry.lengths.find((l) => l.length === chainSel.length) || null
    : null;
  // N3: quote-mode "starting at" is the selected nameplate SKU price; when a
  // chain is added it joins the indicative total so customers see the real
  // combined reference (still not a guaranteed quote).
  const npStartPrice = npResolved?.retail_price || product?.retail_price || 0;
  const quoteStartPrice = npStartPrice + (selectedChain ? selectedChain.price : 0);
  // Effective display values: resolved child SKU is authoritative.
  const cartProduct = npResolved && npResolved.sku !== product?.sku
    ? {
        ...product,
        sku: npResolved.sku,
        retail_price: npResolved.retail_price,
        image_url: npResolved.image_url || product.image_url,
        // Cart/order line names the RESOLVED configuration, never the rep's
        // arbitrary material (e.g. "14K Gold · White Name Plate").
        name: `${npResolved.np_material || ""}${npResolved.np_color ? ` · ${npResolved.np_color}` : ""} Name Plate`,
        seo_title: null,
      }
    : product;

  // Only offer a selector when there is a real choice: 2+ options, or a ring
  // (in-house sizing is offered on single-SKU rings). Single-length
  // pendants/earrings show their size in the spec list instead of a dead
  // one-option dropdown.
  const isSingleSkuRing =
    !!product && !product.np_family && product.category === "Rings" && variantOptions.length <= 1;
  const showVariantSelector =
    !!product && !npFamily && (variantOptions.length > 1 || isSingleSkuRing);

  const stockSize = isSingleSkuRing ? stockSizeOf(product) : null;
  const feeSize =
    isSingleSkuRing && selectedSize && selectedSize !== stockSize ? selectedSize : null;
  const sizedFee = feeSize ? sizingFee(feeSize, stockSize) : 0;
  const engraving =
    product?.engravable && wantEngraving && engraveText.trim()
      ? { text: engraveText.trim(), fee: ENGRAVING_FEE }
      : null;
  const basePrice = cartProduct ? cartProduct.retail_price : 0;
  const totalPrice = product ? basePrice + sizedFee + (engraving ? ENGRAVING_FEE : 0) + (selectedChain ? selectedChain.price : 0) : 0;
  const sizing = feeSize ? { size: feeSize, fee: sizedFee } : null;

  const buyNow = () => {
    if (isNameplate && !plate) {
      toast.error("Tell us the name for your name plate.");
      return;
    }
    if (needsQuote) {
      setQuoteOpen(true);
      return;
    }
    if (wantEngraving && !engraveText.trim()) {
      toast.error("Tell us what to engrave — up to 30 characters.");
      return;
    }
    cart.add(cartProduct, quantity, sizing, engraving, plate,
           !sizing && cartProduct?.variant_axis === "size" && cartProduct?.variant_value
             ? cartProduct.variant_label || `Size ${cartProduct.variant_value}`
             : null);
    if (selectedChain) {
      cart.add({
        sku: selectedChain.sku, name: selectedChain.display_name, seo_title: null,
        retail_price: selectedChain.price, image_url: selectedChain.image_url,
      }, 1);
    }
    navigate("/cart");
  };

  // N2: 8–20 letter names are quoted personally — never ordered online.
  const npQuoteTarget = npResolved || product;
  const submitQuote = async () => {
    const f = quoteForm;
    if (!f.name.trim()) {
      toast.error("Tell us your name.");
      return;
    }
    if (!/.+@.+\..+/.test(f.email.trim())) {
      toast.error("We need a valid email to send your quote.");
      return;
    }
    if (f.phone.trim().length < 5) {
      toast.error("A phone number helps us confirm your quote quickly.");
      return;
    }
    setQuoteBusy(true);
    try {
      await axios.post(`${API}/nameplate-quote`, {
        sku: npQuoteTarget.sku,
        plate_text: plate,
        customer_name: f.name.trim(),
        customer_email: f.email.trim(),
        customer_phone: f.phone.trim(),
        notes: f.notes.trim() || undefined,
        chain: selectedChain ? { sku: selectedChain.sku, length: selectedChain.length } : undefined,
      });
      setQuoteDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your request — please try again.");
    } finally {
      setQuoteBusy(false);
    }
  };

  const keySpecList = product ? keySpecs(product) : [];
  const fullSpecs = product ? specList(product) : [];

  return (
    <main data-testid="product-page">
      <Header />
      <section className="pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <nav className="text-sm text-neutral-500 mb-10 flex items-center gap-1.5" data-testid="product-breadcrumbs">
            <Link to="/" className="hover:text-[#C9A227] transition-colors duration-300">Home</Link>
            <ChevronRight size={14} strokeWidth={1.5} />
            <Link to="/catalog" className="hover:text-[#C9A227] transition-colors duration-300">Catalog</Link>
            <ChevronRight size={14} strokeWidth={1.5} />
            <span className="text-neutral-800">{product ? product.category : "..."}</span>
          </nav>

          {notFound && (
            <div className="text-center py-24" data-testid="product-not-found">
              <h1 className="font-display text-4xl text-neutral-900">Piece not found</h1>
              <p className="text-neutral-600 mt-4">It may have sold or been discontinued.</p>
              <Link to="/catalog" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5">
                Back to the catalog
              </Link>
            </div>
          )}

          {loadError && (
            <div className="text-center py-24" data-testid="product-load-error">
              <h1 className="font-display text-4xl text-neutral-900">This piece is temporarily unavailable</h1>
              <p className="text-neutral-600 mt-4">
                Our catalog had a brief hiccup — the piece itself may be perfectly fine. Please try again in a moment.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5"
                data-testid="product-retry-button"
              >
                Try again
              </button>
            </div>
          )}

          {!product && !notFound && !loadError && (
            // Skeleton reserves the FINAL layout dimensions while the product
            // loads — the old one-line "Loading…" text let the whole page
            // (and the footer) jump ~0.33 CLS when real content arrived.
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 animate-pulse" data-testid="product-loading" aria-busy="true">
              <div>
                <div className="bg-[#EFEAE0] border border-[#E8DCC4] rounded-sm h-[380px] lg:h-[520px]" />
                <div className="mt-4 hidden lg:grid grid-cols-5 gap-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="bg-[#EFEAE0] rounded-sm h-20" />
                  ))}
                </div>
              </div>
              <div className="space-y-5">
                <div className="h-3 w-32 bg-[#EFEAE0] rounded" />
                <div className="h-8 w-4/5 bg-[#EFEAE0] rounded" />
                <div className="h-3 w-40 bg-[#EFEAE0] rounded" />
                <div className="h-10 w-32 bg-[#EFEAE0] rounded" />
                <div className="h-4 w-full bg-[#EFEAE0] rounded" />
                <div className="h-4 w-2/3 bg-[#EFEAE0] rounded" />
                <div className="h-4 w-1/2 bg-[#EFEAE0] rounded" />
                <div className="h-12 w-full bg-[#EFEAE0] rounded-full" />
                <div className="h-12 w-full bg-[#EFEAE0] rounded-full" />
              </div>
            </div>
          )}

          {product && (
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <Reveal key={product.sku}>
                <div key={product.sku}>
                  <div
                    className="relative bg-white border border-[#E8DCC4] rounded-sm overflow-hidden shadow-[0_20px_40px_rgb(201,162,39,0.08)]"
                    data-testid="product-image-frame"
                    // Desktop: move-to-zoom on the full-size source image.
                    onMouseMove={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setZoomOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
                    }}
                    onMouseLeave={() => setZoomOrigin(null)}
                    // Mobile: swipe through the gallery.
                    onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                    onTouchEnd={(e) => {
                      if (touchStartX.current == null) return;
                      const dx = e.changedTouches[0].clientX - touchStartX.current;
                      touchStartX.current = null;
                      const n = product.images?.length || 0;
                      if (n > 1 && Math.abs(dx) > 40) {
                        setActiveImg((cur) => (dx < 0 ? (cur + 1) % n : (cur - 1 + n) % n));
                      }
                    }}
                  >
                    <img
                      src={manualImg || (npResolved && npResolved.sku !== product.sku && npResolved.image_url) || (isMontagePrimary(product) ? product.np_hero_image : null) || (product.images && product.images[activeImg]) || product.image_large || product.image_url || IMAGES.jewelry}
                      alt={title}
                      data-testid="product-image"
                      style={zoomOrigin ? { transformOrigin: zoomOrigin, transform: "scale(1.8)" } : undefined}
                      className={`w-full h-[380px] lg:h-[520px] object-cover transition-transform duration-200 ease-out ${zoomOrigin ? "cursor-zoom-in" : ""}`}
                    />
                    <span
                      data-testid="product-stock-badge"
                      className={`absolute top-4 left-4 rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-[0.12em] font-semibold ${
                        isNameplate || product.qty_avail > 0 ? "bg-[#0D0D0D]/85 text-[#E8CC6E]" : "bg-white/90 text-neutral-600"
                      }`}
                    >
                      {isNameplate
                        ? "Made to Order"
                        : product.qty_avail > 0
                          ? product.qty_avail <= 3 && !(product.variants?.length > 1)
                            ? `Only ${product.qty_avail} left`
                            : "In stock"
                          : "Order-in"}
                    </span>
                  </div>
                  {product.images && product.images.length > 1 && (
                    <div className="mt-4 grid grid-cols-5 gap-3" data-testid="product-gallery">
                      {product.images.slice(0, 5).map((img, idx) => (
                        <button
                          key={idx}
                          data-testid={`product-thumb-${idx}`}
                          onClick={() => { setActiveImg(idx); setManualImg(img); }}
                          className={`rounded-sm overflow-hidden border transition-colors duration-300 ${
                            idx === activeImg ? "border-[#C9A227] ring-1 ring-[#C9A227]" : "border-[#E8DCC4] hover:border-[#C9A227]"
                          }`}
                        >
                          <img
                            src={img.replace("w=1000&h=1000", "w=200&h=200")}
                            alt={`${title} view ${idx + 1}`}
                            loading="lazy"
                            className="w-full h-16 lg:h-20 object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}

                </div>
              </Reveal>

              <Reveal delay={0.1}>
                <p className="text-xs uppercase tracking-[0.2em] text-[#C9A227] font-semibold" data-testid="product-category">
                  {product.category}
                </p>
                {product.lab_grown && (
                  <span
                    data-testid="lab-grown-badge"
                    className="inline-block mt-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#A8871B] bg-[#F5F3EC] border border-[#E8DCC4] rounded-full px-3 py-1"
                  >
                    Lab-grown diamond
                  </span>
                )}
                <h1 className="text-xl md:text-2xl font-semibold text-neutral-900 mt-3 leading-snug" data-testid="product-title">
                  {title}
                </h1>
                <p className="text-xs text-neutral-400 mt-2" data-testid="product-style-number">Style {displaySku(product.sku)}</p>
                {reviewSummary && reviewSummary.count > 0 && (
                  <a href="#product-reviews" className="mt-2 flex items-center gap-2 group w-fit" data-testid="product-stars"
                    onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="product-reviews"]')?.scrollIntoView({ behavior: "smooth" }); }}>
                    <Stars value={reviewSummary.average} size={15} />
                    <span className="text-sm font-semibold text-neutral-800">{reviewSummary.average}</span>
                    <span className="text-xs text-neutral-400 group-hover:text-[#A8871B] transition-colors">
                      {reviewSummary.count} review{reviewSummary.count === 1 ? "" : "s"}
                    </span>
                  </a>
                )}

                {isQuoteMode ? (
                  <div className="mt-6" data-testid="np-quote-pricing-note">
                    <span className="text-xs font-semibold text-[#C9A227] uppercase tracking-[0.15em] block mb-1">Starting at</span>
                    <p className="text-3xl font-bold text-neutral-900" data-testid="product-price">
                      {money(quoteStartPrice)}
                    </p>
                    {selectedChain && (
                      <p className="text-xs text-neutral-400 mt-1" data-testid="np-starting-breakdown">
                        Name plate from {money(npStartPrice)} + chain {money(selectedChain.price)}
                      </p>
                    )}
                    <p className="text-sm text-neutral-500 mt-1.5 leading-relaxed">
                      Final price is confirmed after we review your name and selected options —
                      this starting price is not a guaranteed quote.
                    </p>
                  </div>
                ) : (
                  <div className="mt-6 flex items-end gap-4 flex-wrap">
                    <div>
                      <span className="text-xs font-semibold text-[#C9A227] uppercase tracking-[0.15em] block mb-1">Our price</span>
                      <p className="text-3xl font-bold text-neutral-900" data-testid="product-price">
                        {money(totalPrice)}
                      </p>
                    </div>
                    {(!npFamily || !npResolved || npResolved.sku === product.sku) && (
                      product.msrp && product.msrp > product.retail_price ? (
                        <p className="text-base text-neutral-400 line-through pb-1" data-testid="product-msrp">
                          MSRP {money(product.msrp)}
                        </p>
                      ) : product.compare_at && product.compare_at > product.retail_price ? (
                        <p className="text-base text-neutral-400 line-through pb-1" data-testid="product-compare-at">
                          Compare at {money(product.compare_at)}
                        </p>
                      ) : null
                    )}
                  </div>
                )}
                {sizedFee > 0 && (
                  <p className="text-xs text-neutral-500 mt-2" data-testid="product-sizing-fee-note">
                    {money(basePrice)} + {money(sizedFee)} sizing to size {prettySize(feeSize)} —{" "}
                    <Link to="/refund-policy" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">
                      sized rings are non-refundable
                    </Link>
                  </p>
                )}
                {engraving && (
                  <p className="text-xs text-neutral-500 mt-2" data-testid="product-engraving-fee-note">
                    {money(basePrice)} + {money(ENGRAVING_FEE)} engraving — adds approximately 3–5
                    business days to processing. Engraved pieces are final sale.
                  </p>
                )}

                {!isQuoteMode && (
                  <div className="mt-4">
                    <FinancingNote testid="product-financing" compact />
                  </div>
                )}

                <p className="text-sm text-neutral-600 leading-relaxed mt-5" data-testid="product-description">
                  {describe(product)}
                </p>

                <div className="mt-6 space-y-2 text-sm text-neutral-600" data-testid="product-specs">
                  <p>
                    <span className="font-semibold text-neutral-800">Availability:</span>{" "}
                    {isNameplate
                      ? "Made to order — personalized with your name. Estimated shipping date shown at checkout."
                      : product.qty_avail > 0
                        ? "In stock. Ships insured."
                        : "Available to order. Estimated shipping date shown at checkout."}
                  </p>
                  <p data-testid="product-pickup-note">
                    <span className="font-semibold text-neutral-800">Prefer pickup?</span>{" "}
                    Ship to a Gold Time showroom for pickup — Katy, Copperas Cove or Killeen (choose at checkout)
                  </p>
                </div>

                {(product.metal || keySpecList.length > 0) && (
                  <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="product-key-specs">
                    {(product.np_material || product.metal) && (
                      <div className="flex flex-col gap-0.5">
                        <dt className="text-xs text-neutral-400 uppercase tracking-[0.12em]">Metal</dt>
                        <dd className="text-neutral-900 font-medium">{product.np_material || product.metal}</dd>
                      </div>
                    )}
                    {keySpecList.map((s) => (
                      <div key={s.label} className="flex flex-col gap-0.5">
                        <dt className="text-xs text-neutral-400 uppercase tracking-[0.12em]">{s.label}</dt>
                        <dd className="text-neutral-900 font-medium">{s.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {isNameplate && (
                  <div className="mt-6 bg-[#F5F3EC] border border-[#C9A227]/50 rounded-sm px-5 py-4" data-testid="product-plate-name">
                    <label htmlFor="plate-name-input" className="flex items-center gap-2 font-semibold text-neutral-800 text-sm">
                      <PenLine size={15} strokeWidth={1.5} className="text-[#C9A227]" />
                      Enter the name for your plate
                    </label>
                    <input
                      id="plate-name-input"
                      type="text"
                      value={plateName}
                      onChange={(e) => setPlateName(e.target.value.replace(/[^A-Za-z'\- ]/g, ""))}
                      maxLength={20}
                      placeholder='e.g. "Maria"'
                      data-testid="plate-name-input"
                      className="mt-3 w-full max-w-sm bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300"
                    />
                    <div className="mt-1.5 flex items-start justify-between gap-3 max-w-sm">
                      <p className="text-xs text-neutral-500" data-testid="plate-name-note">
                        {isQuoteMode
                          ? "Letters, apostrophes and hyphens — exactly as you want it. This design is made to fit your name, so final pricing is confirmed personally before anything is made."
                          : "Letters, apostrophes and hyphens — exactly as you want it. Names up to 9 letters can be ordered online; longer names are quoted personally. Every name plate is made to order and final sale."}
                      </p>
                      <span className="text-xs text-neutral-400 shrink-0 pt-px" data-testid="plate-name-count">
                        {plateName.length}/20
                      </span>
                    </div>
                    {needsQuote && !isQuoteMode && (
                      <p className="mt-2 text-xs font-semibold text-[#A8871B]" data-testid="plate-quote-hint">
                        This name is {plateLen} letters — tap Request a Quote below and we&rsquo;ll confirm pricing
                        personally. There&rsquo;s no charge to ask.
                      </p>
                    )}
                  </div>
                )}

                {product.engravable && !isNameplate && (
                  <div className="mt-6 bg-[#F5F3EC] border border-[#E8DCC4] rounded-sm px-5 py-4" data-testid="product-engraving">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={wantEngraving}
                        onChange={(e) => setWantEngraving(e.target.checked)}
                        data-testid="engraving-checkbox"
                        className="mt-1 w-4 h-4 accent-[#C9A227]"
                      />
                      <span>
                        <span className="flex items-center gap-2 font-semibold text-neutral-800 text-sm">
                          <PenLine size={15} strokeWidth={1.5} className="text-[#C9A227]" />
                          Make it yours — add engraving for {money(ENGRAVING_FEE)}
                        </span>
                        <span className="block text-sm text-neutral-600 mt-1 leading-relaxed">
                          Engraving adds approximately 3–5 business days to processing. Engraved pieces are{" "}
                          <Link to="/refund-policy" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">final sale</Link>.
                        </span>
                      </span>
                    </label>
                    {wantEngraving && (
                      <div className="mt-4 pl-7">
                        <input
                          type="text"
                          value={engraveText}
                          onChange={(e) => setEngraveText(e.target.value)}
                          maxLength={30}
                          placeholder='What should it say? e.g. "Maria", "Est. 1994"'
                          data-testid="engraving-text-input"
                          className="w-full max-w-sm bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300"
                        />
                        <p className="text-xs text-neutral-500 mt-1.5" data-testid="engraving-note">
                          Up to 30 characters — we&rsquo;ll confirm the layout with you before engraving.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {product.category === "Wrist" && (
                  <div className="mt-6">
                    <WatchBuyerBanner testid="watch-buyer-banner" />
                  </div>
                )}

                {showVariantSelector && isSingleSkuRing && (
                  <div className="mt-6" data-testid="product-variants">
                    <label htmlFor="variant-select" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-3 block">
                      Select ring size
                    </label>
                    <select
                      id="variant-select"
                      data-testid="variant-select"
                      value={selectedSize ?? stockSize}
                      onChange={(e) => setSelectedSize(parseFloat(e.target.value))}
                      className="w-full max-w-xs bg-white border border-[#E8DCC4] rounded-sm px-4 py-3 text-neutral-900 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300"
                    >
                      {RING_SIZES.map((s) => {
                        const fee = sizingFee(s, stockSize);
                        return (
                          <option key={s} value={s}>
                            {`Size ${prettySize(s)}`}
                            {s === stockSize
                              ? " — as stocked"
                              : ` — sized +${money(fee)}`}
                          </option>
                        );
                      })}
                    </select>
                    <p className="mt-3 text-sm text-neutral-600" data-testid="sizing-policy-note">
                      Every ring is sized before it ships. Sized-to-order rings are{" "}
                      <Link to="/refund-policy" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">
                        non-refundable
                      </Link>
                      . Need a size below 4 or above 12?{" "}
                      <Link
                        to={`/contact?topic=${encodeURIComponent("Ring sizing")}&item=${encodeURIComponent(product.sku)}`}
                        className="text-[#C9A227] font-semibold hover:text-[#A8871B]"
                      >
                        send us the details
                      </Link>{" "}
                      and we'll confirm if we can do it.
                    </p>
                  </div>
                )}

                {npFamily && npFamily.materials.length > 0 && (
                  <div className="mt-6 space-y-5" data-testid="np-configurator">
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-3">Material</p>
                      <div className="flex flex-wrap gap-2">
                        {npFamily.materials.map((mat) => {
                          const key = mat.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
                          const on = npSel.material === mat;
                          return (
                            <button
                              key={mat}
                              type="button"
                              data-testid={`np-material-${key}`}
                              aria-pressed={on}
                              onClick={() => pickNpMaterial(mat)}
                              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-300 ${on ? "border-[#C9A227] bg-[#C9A227]/10 text-[#A8871B]" : "border-[#E8DCC4] text-neutral-600 hover:border-[#C9A227]/60"}`}
                            >
                              {mat}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {npColors.length > 1 && (
                      <div>
                        <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-3">Color</p>
                        <div className="flex flex-wrap gap-2">
                          {npColors.map((c) => {
                            const on = npSel.color === c;
                            return (
                              <button
                                key={c}
                                type="button"
                                data-testid={`np-color-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                aria-pressed={on}
                                onClick={() => setNpSel({ ...npSel, color: c })}
                                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-300 ${on ? "border-[#C9A227] bg-[#C9A227]/10 text-[#A8871B]" : "border-[#E8DCC4] text-neutral-600 hover:border-[#C9A227]/60"}`}
                              >
                                {c}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {npResolved?.np_chain && (
                      <p className="text-sm text-neutral-600 flex items-center gap-2" data-testid="np-chain-note">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A227]" /> {npResolved.np_chain}
                      </p>
                    )}
                    {npResolved && npResolved.sku !== product.sku && (
                      <p className="text-xs text-neutral-400" data-testid="np-resolved-sku">Style {displaySku(npResolved.sku)}</p>
                    )}
                  </div>
                )}

                {isNameplate && chainData && !chainData.chain_included && chainData.styles?.length > 0 && (
                  <div className="mt-6" data-testid="np-chain-selector">
                    <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-3">Add a chain (optional)</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid="np-chain-style-none"
                        aria-pressed={!chainSel.style}
                        onClick={() => setChainSel({ style: null, length: null })}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-300 ${!chainSel.style ? "border-[#C9A227] bg-[#C9A227]/10 text-[#A8871B]" : "border-[#E8DCC4] text-neutral-600 hover:border-[#C9A227]/60"}`}
                      >
                        No chain
                      </button>
                      {chainData.styles.map((s) => (
                        <button
                          key={s.style}
                          type="button"
                          data-testid={`np-chain-style-${s.style}`}
                          aria-pressed={chainSel.style === s.style}
                          onClick={() => setChainSel({ style: s.style, length: null })}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-300 ${chainSel.style === s.style ? "border-[#C9A227] bg-[#C9A227]/10 text-[#A8871B]" : "border-[#E8DCC4] text-neutral-600 hover:border-[#C9A227]/60"}`}
                        >
                          {s.style === "cuban" ? "Cuban / Curb" : s.style.charAt(0).toUpperCase() + s.style.slice(1)}
                        </button>
                      ))}
                    </div>
                    {chainStyleEntry && (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid="np-chain-lengths">
                        {chainStyleEntry.lengths.map((l) => (
                          <button
                            key={l.length}
                            type="button"
                            data-testid={`np-chain-length-${l.length}`}
                            aria-pressed={chainSel.length === l.length}
                            onClick={() => setChainSel({ ...chainSel, length: l.length })}
                            className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors duration-300 ${chainSel.length === l.length ? "border-[#C9A227] bg-[#C9A227]/10 text-[#A8871B]" : "border-[#E8DCC4] text-neutral-600 hover:border-[#C9A227]/60"}`}
                          >
                            {l.length}&Prime; · {money(l.price)}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedChain && (
                      <p className="mt-3 text-sm text-neutral-600 flex items-center gap-2" data-testid="np-chain-selected">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A227]" />
                        {selectedChain.display_name} — {money(selectedChain.price)}
                      </p>
                    )}
                  </div>
                )}

                {isNameplate && chainData && !chainData.chain_included && chainData.styles?.length === 0 && (
                  <p className="mt-6 text-sm text-neutral-500" data-testid="np-chain-unavailable">
                    Matching chain options are not currently available online.{" "}
                    <Link to="/contact" className="text-[#A8871B] underline underline-offset-2">Contact us</Link>{" "}
                    for additional options.
                    {isQuoteMode && " You can also ask for a chain in your quote notes."}
                  </p>
                )}

                {showVariantSelector && !isSingleSkuRing && (
                  <div className="mt-6" data-testid="product-variants">                    <label htmlFor="variant-select" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-3 block">
                      {product.variant_axis === "size" ? "Select ring size" : "Select length"}
                    </label>
                    <select
                      id="variant-select"
                      data-testid="variant-select"
                      value={otherSize ? "other" : product.sku}
                      onChange={(e) => {
                        if (e.target.value === "other") {
                          setOtherSize(true);
                          return;
                        }
                        setOtherSize(false);
                        if (e.target.value !== product.sku) {
                          navigate(slugWithSku(slugParam, product.seo_title || product.name, e.target.value));
                        }
                      }}
                      className="w-full max-w-xs bg-white border border-[#E8DCC4] rounded-sm px-4 py-3 text-neutral-900 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300"
                    >
                      {variantOptions.map((v) => (
                        <option key={v.sku} value={v.sku}>
                          {v.variant_label || v.sku}
                          {v.retail_price !== undefined ? ` — ${money(v.retail_price)}` : ""}
                        </option>
                      ))}
                      {product.variant_axis === "size" && (
                        <option value="other">Another size — ask us</option>
                      )}
                    </select>
                    {otherSize && (
                      <p className="mt-3 text-sm text-neutral-600" data-testid="variant-other-note">
                        Tell us your size and we'll check if this ring can be sized —{" "}
                        <Link
                          to={`/contact?topic=${encodeURIComponent("Ring sizing")}&item=${encodeURIComponent(product.sku)}`}
                          className="text-[#C9A227] font-semibold hover:text-[#A8871B]"
                        >
                          send us your size
                        </Link>
                        .
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-8 flex items-center gap-4">
                  {!needsQuote && (
                    <div className="flex items-center border border-[#E8DCC4] rounded-full bg-white">
                      <button
                        data-testid="product-qty-minus"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-11 h-11 text-neutral-600 hover:text-[#C9A227] transition-colors duration-300 text-lg"
                      >
                        −
                      </button>
                      <span className="w-8 text-center font-semibold" data-testid="product-qty">{quantity}</span>
                      <button
                        data-testid="product-qty-plus"
                        onClick={() => setQuantity(Math.min(10, quantity + 1))}
                        className="w-11 h-11 text-neutral-600 hover:text-[#C9A227] transition-colors duration-300 text-lg"
                      >
                        +
                      </button>
                    </div>
                  )}
                  {needsQuote ? (
                    <button
                      data-testid="product-request-quote"
                      onClick={() => (plate ? setQuoteOpen(true) : toast.error("Tell us the name for your name plate."))}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_20px_40px_rgb(201,162,39,0.20)]"
                    >
                      <FileText size={16} strokeWidth={1.5} /> Request a Quote
                    </button>
                  ) : (
                    <button
                      data-testid="product-add-to-cart"
                      onClick={addToCart}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#C9A227] text-[#C9A227] text-sm font-semibold px-6 py-3.5 hover:bg-[#F5F3EC] transition-colors duration-300"
                    >
                      <ShoppingBag size={16} strokeWidth={1.5} /> Add to cart
                    </button>
                  )}
                  <WishlistButton sku={product.sku} testid="product-wishlist" className="w-12 h-12 border border-[#E8DCC4] bg-white hover:border-[#C9A227]" />
                </div>

                {needsQuote ? (
                  <p className="mt-3 text-xs text-neutral-500" data-testid="quote-not-order-note">
                    A quote request is free — it is not an order, and no payment is taken.
                  </p>
                ) : (
                  <button
                    data-testid="product-buy-now"
                    onClick={buyNow}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_20px_40px_rgb(201,162,39,0.20)]"
                  >
                    <Zap size={16} strokeWidth={1.5} /> Buy now
                  </button>
                )}

                <a
                  href={`${SITE.whatsappHref}?text=${encodeURIComponent(
                    `Hello Gold Time, I'm interested in the ${title} (SKU ${product.sku}) listed at ${money(product.retail_price)}.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-whatsapp"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-neutral-600 hover:text-[#C9A227] transition-colors duration-300"
                >
                  <MessageCircle size={16} strokeWidth={1.5} /> Questions? Ask us on WhatsApp
                </a>

                <div className="mt-8 pt-6 border-t border-[#E8DCC4]">
                  <TrustBar testid="product-trust-bar" />
                </div>

                {fullSpecs.length > 0 && (
                  <details className="mt-8 group" data-testid="product-attributes">
                    <summary
                      className="cursor-pointer list-none flex items-center justify-between border border-[#E8DCC4] rounded-sm px-5 py-3.5 text-xs uppercase tracking-[0.2em] text-[#C9A227] font-semibold hover:border-[#C9A227] transition-colors duration-300"
                      data-testid="product-specs-toggle"
                    >
                      View Full Specifications
                      <ChevronRight size={15} strokeWidth={1.5} className="rotate-90 group-open:-rotate-90 transition-transform duration-300" />
                    </summary>
                    <dl className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2">
                      {fullSpecs.map((s, idx) => (
                        <div key={`${s.label}-${idx}`} className="flex justify-between gap-4 text-sm border-b border-[#F5F3EC] pb-2" data-testid={`product-attr-${idx}`}>
                          <dt className="text-neutral-500">{s.label}</dt>
                          <dd className="text-neutral-900 font-medium text-right">{s.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                )}
              </Reveal>
            </div>
          )}

          {product && (
            <div className="mt-14 max-w-3xl" data-testid="product-about">
              <h2 className="text-xs uppercase tracking-[0.2em] text-[#C9A227] font-semibold mb-4">
                About this piece
              </h2>
              <p className="text-sm md:text-base text-neutral-600 leading-relaxed">{aboutPiece(product).main}</p>
              <p className="text-sm md:text-base text-neutral-600 leading-relaxed mt-3">{aboutPiece(product).closer}</p>
            </div>
          )}

          {product && <ProductReviews sku={product.sku} />}

          {product && <RecentlyViewed excludeSku={product.sku} />}

          {product && related.length > 0 && (
            <div className="mt-20" data-testid="related-products">
              <Eyebrow>Keep looking</Eyebrow>
              <h2 className="font-display text-2xl md:text-3xl font-light text-neutral-900 mt-3">
                You may also <span className="text-[#C9A227]">like</span>
              </h2>
              <div className="mt-8 flex gap-5 overflow-x-auto pb-4 -mx-6 px-6 sm:mx-0 sm:px-0">
                {related.map((p) => (
                  <Link
                    key={p.sku}
                    to={productPath(p)}
                    onClick={() => trackSelectItem(p, "Related products")}
                    data-testid={`related-card-${p.sku}`}
                    className="group w-[190px] sm:w-[220px] shrink-0 bg-white border border-[#E8DCC4] rounded-sm overflow-hidden hover:border-[#C9A227] hover:shadow-[0_16px_36px_rgba(201,162,39,0.15)] transition-[border-color,box-shadow] duration-300"
                  >
                    <img
                      src={p.image_url}
                      alt={displayTitle(p)}
                      loading="lazy"
                      className="w-full h-[160px] object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                    />
                    <div className="p-3.5">
                      <p className="text-xs font-semibold text-neutral-900 leading-snug line-clamp-2">{displayTitle(p)}</p>
                      <p className="text-sm font-bold text-[#C9A227] mt-1.5">{money(p.retail_price)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {product && isNameplate && (
        <Dialog open={quoteOpen} onOpenChange={(o) => { setQuoteOpen(o); if (!o) setQuoteDone(false); }}>
          <DialogContent className="bg-white border-[#E8DCC4] max-w-md" data-testid="np-quote-dialog">
            {quoteDone ? (
              <div data-testid="np-quote-success" className="text-center py-2">
                <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#C9A227]/10">
                  <FileText size={20} strokeWidth={1.5} className="text-[#C9A227]" />
                </span>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl text-neutral-900 text-center">Quote request sent</DialogTitle>
                </DialogHeader>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                  Thank you — we&rsquo;ve received your request for &ldquo;{plate}&rdquo;. This is a request for a
                  quote, not an order, and not a guaranteed price. We&rsquo;ll confirm the details and exact pricing
                  with you personally before anything is made.
                </p>
                <button
                  data-testid="np-quote-close"
                  onClick={() => setQuoteOpen(false)}
                  className="mt-6 inline-flex items-center justify-center rounded-full border border-[#C9A227] text-[#A8871B] text-sm font-semibold px-8 py-2.5 hover:bg-[#F5F3EC] transition-colors duration-300"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl text-neutral-900">Request a Quote</DialogTitle>
                  <DialogDescription className="text-sm text-neutral-500">
                    {isQuoteMode
                      ? "This design is sized to your name — final pricing is confirmed after we review your requested name and configuration. This is a quote request, not an order, and no payment is taken."
                      : "Names over 9 letters are priced personally. Send us your configuration — this is a quote request, not an order, and no payment is taken."}
                  </DialogDescription>
                </DialogHeader>
                <div className="bg-[#F5F3EC] border border-[#E8DCC4] rounded-sm px-4 py-3 text-sm" data-testid="np-quote-config">
                  <p className="font-semibold text-neutral-900">&ldquo;{plate}&rdquo; <span className="font-normal text-neutral-500">({plateLen} letters)</span></p>
                  <p className="text-neutral-600 mt-1">
                    {[npQuoteTarget?.np_material, npQuoteTarget?.np_color].filter(Boolean).join(" · ") || title}
                  </p>
                  <p className="text-neutral-500 text-xs mt-1">Style {displaySku(npQuoteTarget?.sku || product.sku)}</p>
                  {npQuoteTarget?.np_chain && (
                    <p className="text-neutral-500 text-xs mt-1">{npQuoteTarget.np_chain}</p>
                  )}
                  {selectedChain && (
                    <p className="text-neutral-600 text-xs mt-1" data-testid="np-quote-chain">
                      Optional chain: {selectedChain.display_name} — {money(selectedChain.price)} (current chain price)
                    </p>
                  )}
                  {!isQuoteMode && (
                    <p className="text-xs text-neutral-400 mt-2">
                      Current online price {money(npQuoteTarget?.retail_price || product.retail_price)} — for reference only, not a guaranteed quote.
                    </p>
                  )}
                </div>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={quoteForm.name}
                    onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="np-quote-name-input"
                    className="w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]"
                  />
                  <input
                    type="email"
                    value={quoteForm.email}
                    onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                    placeholder="Email"
                    data-testid="np-quote-email-input"
                    className="w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]"
                  />
                  <input
                    type="tel"
                    value={quoteForm.phone}
                    onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                    placeholder="Phone"
                    data-testid="np-quote-phone-input"
                    className="w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]"
                  />
                  <textarea
                    value={quoteForm.notes}
                    onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                    placeholder="Anything we should know? (optional)"
                    rows={3}
                    data-testid="np-quote-notes-input"
                    className="w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]"
                  />
                </div>
                <button
                  data-testid="np-quote-submit"
                  onClick={submitQuote}
                  disabled={quoteBusy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] transition-[background] duration-300 disabled:opacity-60"
                >
                  {quoteBusy ? "Sending…" : "Send quote request"}
                </button>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {product && (
        <div
          data-testid="sticky-atc-bar"
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#E8DCC4] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-4 shadow-[0_-10px_30px_rgba(0,0,0,0.08)]"
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs text-neutral-500 truncate">{title}</p>
            <p className="text-base font-bold text-neutral-900">{isQuoteMode ? `Starting at ${money(quoteStartPrice)}` : money(totalPrice)}</p>
          </div>
          <button
            data-testid="sticky-add-to-cart"
            onClick={needsQuote ? () => (plate ? setQuoteOpen(true) : toast.error("Tell us the name for your name plate.")) : addToCart}
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3 shadow-[0_10px_24px_rgb(201,162,39,0.25)]"
          >
            {needsQuote ? (
              <><FileText size={15} strokeWidth={1.5} /> Request a Quote</>
            ) : (
              <><ShoppingBag size={15} strokeWidth={1.5} /> Add to cart</>
            )}
          </button>
        </div>
      )}
      {/* spacer so the sticky mobile add-to-cart bar never covers the footer — rendered during loading too, so the footer doesn't jump when it appears */}
      {!notFound && <div className="h-20 lg:hidden" aria-hidden="true" />}
      <Footer />
      <WhatsAppFab raised />
    </main>
  );
}
